<template>
  <v-app>
    <v-system-bar
      v-if="updateAvailable || !isProd"
      :color="updateAvailable ? 'info' : 'warning'"
      id="systembar"
      fixed
      style="z-index: 20"
    >
      <template v-if="updateAvailable">
        <v-icon>mdi-update</v-icon>
        <span>{{ $t("navigation.systemBar.updateAvailable") }}</span>
        <v-spacer></v-spacer>
        <v-btn x-small text @click="applyUpdate" dark>
          {{ $t("navigation.systemBar.updateAction") }}
        </v-btn>
      </template>
      <template v-else>
        <v-icon>mdi-alert-outline</v-icon>
        <v-tooltip bottom>
          <template v-slot:activator="{ on }">
            <span v-on="on">Development Environment</span>
          </template>
          <span>Development environment is strictly meant for testing purposes and is not bound to any SLA</span>
        </v-tooltip>
        <v-spacer></v-spacer>
      </template>
    </v-system-bar>

    <the-navigation-base-v2 v-if="!isHeadless && isNavV2" class="no-print"></the-navigation-base-v2>
    <the-navigation-base v-else-if="!isHeadless" class="no-print"></the-navigation-base>

    <v-app-bar
      v-if="!isHeadless"
      app
      flat
      :color="$vuetify.theme.dark ? backgroundDarkColor : backgroundColor"
      class="no-print"
    >
      <v-app-bar-nav-icon
        data-test-hamburger
        @click.stop="toggleDrawer"
        :color="$vuetify.theme.dark ? 'white' : 'grey darken-4'"
        aria-label="Menu"
      ></v-app-bar-nav-icon>

      <quick-link-menu v-if="isAuthenticated && isQuicklinks && userLoaded" @click="$refs.commandPalette.open()" />

      <v-spacer />

      <v-toolbar-title>
        <v-img :src="tryHeaderLogo" contain style="max-height: 40px" @click="logoClick" />
      </v-toolbar-title>
      <template v-if="isAuthenticated">
        <v-spacer />
        <v-btn plain text @click="$refs.partnerReportDialog.open()" v-if="isNavV2 && canCreateReport && isBroker">
          <v-icon>mdi-plus</v-icon>
        </v-btn>

        <command-palette ref="commandPalette"></command-palette>
        <partner-report-dialog ref="partnerReportDialog" />
        <v-badge
          v-if="isInboxEnabled && readMessage && userLoaded"
          :value="!!unreadMessagesCount"
          :content="`${unreadMessagesCount}`"
          :offset-x="26"
          :offset-y="26"
          top
          right
        >
          <v-btn plain text @click="goToInbox">
            <v-icon>mdi-email-outline</v-icon>
          </v-btn>
        </v-badge>
        <profile-menu :isEndUser="isEndUser" />
      </template>
      <template v-else-if="showLoginButton">
        <v-spacer />
        <v-btn :color="color" v-if="!isAuthenticated" @click="goToLogin" icon aria-label="Login">
          <v-icon>mdi-account</v-icon>
        </v-btn>
      </template>
      <template v-else>
        <v-spacer class="ml-12" />
      </template>

      <v-progress-linear v-if="progress" :value="progress" absolute bottom :color="color" aria-label="Progress">
      </v-progress-linear>
    </v-app-bar>

    <!-- Sizes your content based upon application components -->
    <v-main>
      <v-divider v-if="displayDivider" class="no-print" />
      <slot />
      <PartnerFooter
        v-if="partner && showFooter && !isHeadless"
        :partnerProp="partner"
        :color="color"
        class="d-none d-md-flex"
      />
    </v-main>
  </v-app>
</template>

<script lang="ts">
import PartnerFooter from "@/components/partner/PartnerFooter.vue";
import ProfileMenu from "@/components/profile/ProfileMenu.vue";
import TheNavigationBase from "@/layouts/TheNavigationBase.vue";
import { AssetRepository } from "@/lib/AssetRepository";
import { AppContextEnum } from "@/lib/enum/appContext.enum";
import eventBus from "@/lib/eventBus";
import { getDefaultPartnerColor } from "@/lib/getDefaultPartnerColor";
import PermissionMixin from "@/mixins/PermissionMixin.vue";
import { PartnerMessageDataAccessLayer } from "@/store/modules/access-layers/partner-message.access-layer";
import { ConfigModule } from "@/store/modules/config";
import { FeatureModule } from "@/store/modules/feature.store";
import { UserModule } from "@/store/modules/me-user.store";
import { NavigationModule } from "@/store/modules/navigation.store";
import { NotificationModule } from "@/store/modules/notification.store";
import { PartnerModule } from "@/store/modules/partner";
import { ReportModule } from "@/store/modules/report.store";
import { UiModule } from "@/store/modules/ui.store";
import { Component, Prop } from "vue-property-decorator";
import QuickLinkMenu from "./navigation/QuickLinkMenu.vue";
import CommandPalette from "./navigation/CommandPalette.vue";
import ThePartnerSelectorList from "./navigation/ThePartnerSelectorList.vue";
import { MessageFolderEnum } from "@/lib/enum/message-folder.enum";
import TheNavigationBaseV2 from "./TheNavigationBaseV2.vue";
import PartnerReportDialog from "@/components/partner/PartnerReportCreateDialog.vue";
import { PartnerTypeEnum } from "@/store/enum/partner/partner.type.enum";
import { applyUpdate as applyServiceWorkerUpdate } from "@/registerServiceWorker";

@Component({
  components: {
    ThePartnerSelectorList,
    TheNavigationBase,
    TheNavigationBaseV2,
    PartnerFooter,
    ProfileMenu,
    QuickLinkMenu,
    CommandPalette,
    PartnerReportDialog
  }
})
export default class TheLayoutBase extends PermissionMixin {
  @Prop({ default: "white" })
  backgroundColor!: string;

  @Prop({ default: false })
  showLoginButton!: boolean;

  @Prop({ default: "grey darken-3" })
  backgroundDarkColor!: string;

  @Prop({ default: 0 })
  progress!: number;

  @Prop({ default: true })
  showFooter!: boolean;

  @Prop({ default: false })
  displayDivider!: boolean;

  @Prop({})
  headerLogo?: string;

  @Prop({ default: false })
  isEndUser!: boolean;

  @Prop({ default: false })
  isFillHeight!: boolean;

  get isNavV2() {
    return ConfigModule.appContext === AppContextEnum.REPORT_PORTAL;
  }

  get isBroker() {
    return this.partner.partnerType === PartnerTypeEnum.BROKER;
  }

  get fillHeight() {
    if (!this.isFillHeight) {
      return "";
    }

    return "fill-height";
  }

  get userLoaded() {
    return UserModule.user._id;
  }

  get color() {
    return getDefaultPartnerColor();
  }

  get isQuicklinks(): boolean {
    return NavigationModule.navigationConfiguration.isQuicklinks;
  }

  get isProd() {
    return ConfigModule.isProd;
  }

  get updateAvailable() {
    return ConfigModule.updateAvailable;
  }

  applyUpdate() {
    applyServiceWorkerUpdate();
  }

  get navigationSettings() {
    return ConfigModule.navigationSettings;
  }

  get partner() {
    return PartnerModule.partner ?? ReportModule.partner;
  }

  get partners() {
    return PartnerModule.partners;
  }

  set drawer(val: any) {
    UiModule.setDrawer(val);
  }

  get drawer() {
    return UiModule.drawer;
  }

  get notificationCount() {
    return NotificationModule.notifications.length;
  }

  get isHeadless() {
    return ConfigModule.headless;
  }

  get isMobile() {
    return this.$vuetify.breakpoint.smAndDown;
  }

  get displayPartnerName() {
    if (!this.isAuthenticated) {
      return false;
    }

    switch (this.$vuetify.breakpoint.name) {
      case "xs":
        return false;
      case "sm":
        return false;
      case "md":
        return true;
      case "lg":
        return true;
      case "xl":
        return true;
    }

    return false;
  }

  get breadcrumbs() {
    return [
      {
        text: this.companyName,
        disabled: true
      }
    ];
  }

  get companyName() {
    return PartnerModule.partner?.companyName || "";
  }

  get unreadMessagesCount() {
    return PartnerMessageDataAccessLayer.entities.filter((e) => !e.isRead && e.folder === MessageFolderEnum.INBOX)
      .length;
  }

  toggleDrawer() {
    UiModule.setDrawer(!UiModule.drawer);
  }

  goToLogin() {
    this.$router.push({ path: "/login" });
  }

  logoClick() {
    this.$emit("logoClick", true);
    this.$log.info("Logo clicked");
  }

  /**
   * Try to set the Header Logo by priority
   *
   */
  get tryHeaderLogo() {
    if (this.headerLogo) {
      return this.headerLogo;
    }

    if (this.partner.settings?.reportSettings?.logoUrl) {
      return this.partner.settings?.reportSettings?.logoUrl;
    }

    if (this.partner.settings?.logoUrl) {
      return this.partner.settings?.logoUrl;
    }

    return this.logoUrl;
  }

  get logoUrl() {
    return AssetRepository.getAsset(ConfigModule.darkMode, ConfigModule._portalLogo);
  }

  get isAuthenticated() {
    return UserModule.isAuthenticated;
  }

  get isNotification() {
    return NotificationModule.isNotification;
  }

  notificationButtonClicked(event: any) {
    eventBus.$emit("notificationButtonClicked", event);
    this.$router.push({ name: "ThgOverview" });
  }

  get isInboxEnabled() {
    return (
      [AppContextEnum.THG_PORTAL, AppContextEnum.REPORT_PORTAL].includes(ConfigModule.appContext) &&
      FeatureModule.isInboxEnabled
    );
  }

  goToInbox() {
    this.$router.push({ name: "PartnerMessageInboxList", params: { partnerId: this.partner.id } });
  }

  items = [
    {
      text: "Login",
      disabled: false,
      href: "#/login"
    },
    {
      text: "Impressum",
      disabled: false,
      href: "#/impressum"
    },
    {
      text: "Datenschutz",
      disabled: false,
      href: "#/datenschutz"
    },
    {
      text: "AGB",
      disabled: false,
      href: "#/agbs"
    }
  ];
}
</script>

<style>
.progressContainer {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  width: 100%;
  padding-left: 28px !important;
  padding-right: 28px !important;
}

.v-toolbar__content {
  padding: 4px 28px !important;
}
@media print {
  .no-print,
  .no-print * {
    display: none !important;
  }
  .print-at-pagestart {
    margin-top: -40px;
  }
}
</style>
